import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { filteredAgenciesQuery } from '../../api/queries';
import { Category, ItemConnectionSort } from '../../graphql/graphql';
import {
  AGENCY_FILTER_ALL_DEAL_TYPES,
  AGENCY_FILTER_CATEGORY_ALL_ID,
  AGENCY_PAGE_DESKTOP_CARDS_LIMIT,
  AgencyFilterDealTypeEnum,
  COMMERCIAL_CATEGORY_SLUGS,
  DEFAULT_DESKTOP_CARDS_LIMIT
} from '../constants/agencyPage';
import { isLeasedCategory } from './common';
import { getI18nForLang } from './getI18nForLang';

export const DEAL_TYPE_TO_SLUG: Record<AgencyFilterDealTypeEnum, string> = {
  [AgencyFilterDealTypeEnum.lease]: 'kiraye',
  [AgencyFilterDealTypeEnum.sale]: 'alqi-satqi'
};

export const SLUG_TO_DEAL_TYPE: Record<string, AgencyFilterDealTypeEnum> = {
  ...Object.fromEntries(
    Object.entries(DEAL_TYPE_TO_SLUG).map(([k, v]) => [
      v,
      k as AgencyFilterDealTypeEnum
    ])
  ),
  icare: AgencyFilterDealTypeEnum.lease
};

const SSR_CARDS_LIMIT = AGENCY_PAGE_DESKTOP_CARDS_LIMIT * 2;

type CategoryIndex = {
  bySlug: Map<string, string>;
  byId: Map<string, string>;
};

type CategoryLike = Pick<Category, 'id' | 'slug'>;

const buildCategoryIndex = (categories: CategoryLike[]): CategoryIndex =>
  categories.reduce<CategoryIndex>(
    (acc, { slug, id }) => {
      const lastSlug = slug?.split('/').pop();
      if (lastSlug) {
        acc.bySlug.set(lastSlug, id);
        acc.byId.set(id, lastSlug);
      }
      return acc;
    },
    { bySlug: new Map(), byId: new Map() }
  );

export const parseAgencySegments = (
  segments: string[] = [],
  categories: CategoryLike[] = []
): { dealType: AgencyFilterDealTypeEnum | null; category: string | null } => {
  const { bySlug } = buildCategoryIndex(categories);

  return segments.reduce(
    (acc, segment) => {
      if (Object.hasOwn(SLUG_TO_DEAL_TYPE, segment)) {
        acc.dealType = SLUG_TO_DEAL_TYPE[segment];
      } else {
        const categoryId = bySlug.get(segment);
        if (categoryId) acc.category = categoryId;
      }
      return acc;
    },
    {
      dealType: null as AgencyFilterDealTypeEnum | null,
      category: null as string | null
    }
  );
};

type BuildAgencyUrlParams = {
  agencySlug: string;
  clickedId: string;
  activeLeasedCategory: AgencyFilterDealTypeEnum[];
  categories: CategoryLike[];
};

export const getLeaseSlug = (categorySlug: string | null): string =>
  categorySlug && COMMERCIAL_CATEGORY_SLUGS.has(categorySlug)
    ? 'icare'
    : 'kiraye';

export const buildAgencyUrl = ({
  agencySlug,
  clickedId,
  activeLeasedCategory,
  categories
}: BuildAgencyUrlParams): string => {
  const basePath = `/agentlikler/${agencySlug}`;
  const { byId } = buildCategoryIndex(categories);

  const isDealType = clickedId in DEAL_TYPE_TO_SLUG;

  const dealType: AgencyFilterDealTypeEnum | null = (() => {
    if (isDealType) return clickedId as AgencyFilterDealTypeEnum;
    if (activeLeasedCategory.length === 1) return activeLeasedCategory[0];
    return null;
  })();

  const categorySlug = !isDealType ? (byId.get(clickedId) ?? null) : null;

  const dealSlug = (() => {
    if (dealType === AgencyFilterDealTypeEnum.lease)
      return getLeaseSlug(categorySlug);
    if (dealType) return DEAL_TYPE_TO_SLUG[dealType];
    return null;
  })();

  const parts = [dealSlug, categorySlug].filter(Boolean);

  return parts.length ? `${basePath}/${parts.join('/')}` : basePath;
};

export const getAgencyFilterCardsLimit = (
  isLarge: boolean,
  isSmall: boolean,
  isClient: boolean,
  desktopCardsPerLine = DEFAULT_DESKTOP_CARDS_LIMIT
): number => {
  if (isLarge || !isClient) return desktopCardsPerLine * 2;
  if (isSmall) return 4;
  return 6;
};

type AgencyItemsFilter = {
  agencyId: string;
  categoryId?: string | null;
  dealType?: AgencyFilterDealTypeEnum | null;
};

const buildItemsFilter = ({
  agencyId,
  categoryId,
  dealType
}: AgencyItemsFilter) => ({
  agencyId,
  ...(categoryId ? { categoryId } : {}),
  ...(dealType ? { leased: dealType === AgencyFilterDealTypeEnum.lease } : {})
});

const prefetchItems = (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  filter: AgencyItemsFilter,
  first: number
) =>
  apolloClient.query({
    query: filteredAgenciesQuery,
    variables: {
      filter: buildItemsFilter(filter),
      first,
      sort: ItemConnectionSort.BumpedAtDesc
    }
  });

type GetAgencyInitialItemsParams = {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  agencyId: string;
  dealType: AgencyFilterDealTypeEnum | null;
  category: string | null;
  itemCategories: { id: string }[];
};

export const getAgencyInitialItems = async ({
  apolloClient,
  agencyId,
  dealType,
  category,
  itemCategories
}: GetAgencyInitialItemsParams): Promise<{ totalCount: number }> => {
  if (dealType && category) {
    const result = await prefetchItems(
      apolloClient,
      { agencyId, categoryId: category, dealType },
      SSR_CARDS_LIMIT * 2
    );
    return {
      totalCount: result.data?.itemsConnection?.totalCount ?? 0
    };
  }

  if (category) {
    const [leaseResult, saleResult] = await Promise.all([
      prefetchItems(
        apolloClient,
        {
          agencyId,
          categoryId: category,
          dealType: AgencyFilterDealTypeEnum.lease
        },
        SSR_CARDS_LIMIT
      ),
      prefetchItems(
        apolloClient,
        {
          agencyId,
          categoryId: category,
          dealType: AgencyFilterDealTypeEnum.sale
        },
        SSR_CARDS_LIMIT
      )
    ]);
    return {
      totalCount:
        (leaseResult.data?.itemsConnection?.totalCount ?? 0) +
        (saleResult.data?.itemsConnection?.totalCount ?? 0)
    };
  }

  if (dealType) {
    const results = await Promise.all(
      itemCategories
        .slice(0, 2)
        .map(({ id }) =>
          prefetchItems(
            apolloClient,
            { agencyId, categoryId: id, dealType },
            SSR_CARDS_LIMIT
          )
        )
    );
    return {
      totalCount: results.reduce(
        (sum, r) => sum + (r.data?.itemsConnection?.totalCount ?? 0),
        0
      )
    };
  }

  return { totalCount: 0 };
};

const resolveSeoKeys = (
  base: string,
  dealKey: string | null,
  categorySlug: string | null
) => {
  if (dealKey && !categorySlug) {
    const dealBase = `${base}.deal.${dealKey}`;

    return {
      phraseKey: `${dealBase}.title_phrase`,
      descriptionKey: `${dealBase}.description`,
      h1Key: `${dealBase}.h1`
    };
  }

  const categoryBase = `${base}.category.${categorySlug}`;
  const withDeal = dealKey ? `_with_${dealKey}` : '';

  return {
    phraseKey: `${categoryBase}.title_phrase${withDeal}`,
    descriptionKey: `${categoryBase}.description${dealKey ? `_${dealKey}` : ''}`,
    h1Key: `${categoryBase}.h1${withDeal}`
  };
};

export const buildAgencySeoContent = (
  name: string | null | undefined,
  lng: string,
  segments: string[]
) => {
  const { t } = getI18nForLang(lng);
  const agencyVars = { agency_name: name };

  const dealSlug =
    segments.find(s => Object.hasOwn(SLUG_TO_DEAL_TYPE, s)) ?? null;
  const categorySlug =
    segments.find(s => !Object.hasOwn(SLUG_TO_DEAL_TYPE, s)) ?? null;
  const dealKey = dealSlug ? SLUG_TO_DEAL_TYPE[dealSlug] : null;

  if (!dealKey && !categorySlug) {
    return {
      title: name ? t('pages.agency.meta_title', agencyVars) : null,
      description: name ? t('pages.agency.meta_description', agencyVars) : null,
      h1: name ? t('pages.agency.title', { name }) : null
    };
  }

  const base = 'pages.agency.seo';
  const { phraseKey, descriptionKey, h1Key } = resolveSeoKeys(
    base,
    dealKey,
    categorySlug
  );

  return {
    title: t(`${base}.title_template`, { ...agencyVars, phrase: t(phraseKey) }),
    description: t(descriptionKey, agencyVars),
    h1: t(`${base}.h1_template`, { ...agencyVars, suffix: t(h1Key) })
  };
};

export const getAgencyFilterCategoryStatName = (
  slug: string,
  isCurrentItemPage: boolean
) => {
  const slugPart = slug.includes('/') ? slug.split('/').pop() : slug;
  const prefix = isCurrentItemPage ? 'current-item' : 'agency';
  return `${prefix}-filter-${slugPart}`;
};

export const isValidAgencyUrl = (
  segments: string[],
  category: string | null
) => {
  const dealSegmentCount = segments.filter(s =>
    Object.hasOwn(SLUG_TO_DEAL_TYPE, s)
  ).length;
  const nonDealSegments = segments.filter(
    s => !Object.hasOwn(SLUG_TO_DEAL_TYPE, s)
  );
  return (
    dealSegmentCount <= 1 &&
    nonDealSegments.length <= 1 &&
    !(nonDealSegments.length > 0 && category === null)
  );
};

export const getCategorySlug = (
  category: string | null,
  itemCategories: CategoryLike[]
) =>
  category
    ? (itemCategories
        .find(c => c.id === category)
        ?.slug?.split('/')
        .pop() ?? null)
    : null;

export const getCanonicalAgencyPath = (
  slug: string,
  segments: string[],
  dealType: AgencyFilterDealTypeEnum | null,
  categorySlug: string | null
) => {
  let canonicalDealSlug: string | null = null;
  if (dealType === AgencyFilterDealTypeEnum.lease) {
    canonicalDealSlug = getLeaseSlug(categorySlug);
  } else if (dealType) {
    canonicalDealSlug = DEAL_TYPE_TO_SLUG[dealType];
  }

  const canonicalParts = [canonicalDealSlug, categorySlug].filter(Boolean);

  if (canonicalParts.join('/') === segments.join('/')) return null;

  return canonicalParts.length
    ? `/agentlikler/${slug}/${canonicalParts.join('/')}`
    : `/agentlikler/${slug}`;
};

type EmptyResultsParams = {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  agencyId: string;
  dealType: AgencyFilterDealTypeEnum | null;
  category: string | null;
  categorySlug: string | null;
  slug: string;
  itemCategories: Category[];
};

export const getEmptyResultsRedirectDestination = async ({
  apolloClient,
  agencyId,
  dealType,
  category,
  categorySlug,
  slug,
  itemCategories
}: EmptyResultsParams) => {
  const { totalCount } = await getAgencyInitialItems({
    apolloClient,
    agencyId,
    dealType,
    category,
    itemCategories
  });

  if (totalCount === 0) {
    return dealType && category && categorySlug
      ? `/agentlikler/${slug}/${categorySlug}`
      : `/agentlikler/${slug}`;
  }

  return null;
};

export const resolveAgencyBlockParams = (
  category: Category,
  activeCategoryId: string,
  activeLeasedCategory: string[],
  baseLimit: number
) => {
  if (activeCategoryId === AGENCY_FILTER_CATEGORY_ALL_ID) {
    return {
      isLeased: false,
      limit: baseLimit,
      actualCategory: category,
      activeId: activeCategoryId
    };
  }

  if (isLeasedCategory(activeCategoryId) && !isLeasedCategory(category.id)) {
    return {
      isLeased: true,
      limit: activeLeasedCategory.length > 1 ? baseLimit : 20,
      actualCategory: category,
      activeId: activeCategoryId
    };
  }

  if (
    activeLeasedCategory.includes(category.id) &&
    !isLeasedCategory(activeCategoryId) &&
    isLeasedCategory(category.id)
  ) {
    return {
      isLeased: true,
      limit: activeLeasedCategory.length > 1 ? baseLimit : 20,
      actualCategory: { id: activeCategoryId },
      activeId: category.id
    };
  }

  return null;
};

export const resolveHrefLeasedCategory = (
  activeId: string,
  activeCategoryId: string,
  activeLeasedCategory: string[]
) => {
  if (activeId === AGENCY_FILTER_CATEGORY_ALL_ID) {
    return AGENCY_FILTER_ALL_DEAL_TYPES;
  }
  if (isLeasedCategory(activeCategoryId)) {
    return [activeCategoryId as AgencyFilterDealTypeEnum];
  }
  return activeLeasedCategory as AgencyFilterDealTypeEnum[];
};
